import React, { useState, useEffect } from 'react';
import { DateRange } from 'Components/DateRange';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  Stack,
  Paper,
  Button,
  TextField,
  MenuItem,
  LinearProgress
} from '@mui/material';
import { getRequest } from 'Includes/api/apicall';
import { GET_URL } from 'Includes/urls';
import { filterFilledReviewSections, stripHtml } from './visitConcepts';

const TRUNCATE_LEN = 40;

function ExpandableCell({ value, isHtml = false }) {
  const [expanded, setExpanded] = React.useState(false);

  const displayValue = isHtml ? filterFilledReviewSections(value) : String(value || '').trim();
  if (!displayValue) return '-';

  const plainText = isHtml ? stripHtml(displayValue) : displayValue;
  const needsMore = plainText.length > TRUNCATE_LEN;

  const handleToggle = (e) => {
    e.stopPropagation();
    setExpanded((prev) => !prev);
  };

  const htmlContentSx = {
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: 'text.primary',
    '& strong': { fontWeight: 700 },
    '& b': { fontWeight: 700 },
    '& p': { margin: '0 0 6px' },
  };

  if (!needsMore) {
    if (isHtml) {
      return (
        <Box sx={{ py: 0.5, width: '100%', ...htmlContentSx }} dangerouslySetInnerHTML={{ __html: displayValue }} />
      );
    }
    return (
      <Typography variant="body2" sx={{ py: 0.5, whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
        {displayValue}
      </Typography>
    );
  }

  return (
    <Box sx={{ py: 0.5, width: '100%' }}>
      {expanded ? (
        isHtml ? (
          <Box sx={htmlContentSx} dangerouslySetInnerHTML={{ __html: displayValue }} />
        ) : (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
            {displayValue}
          </Typography>
        )
      ) : (
        <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
          {`${plainText.slice(0, TRUNCATE_LEN)}...`}
        </Typography>
      )}
      <Button
        size="small"
        sx={{ p: 0, minWidth: 'auto', textTransform: 'none', mt: 0.25, fontWeight: 600 }}
        onClick={handleToggle}
      >
        {expanded ? 'Show less' : 'Show more'}
      </Button>
    </Box>
  );
}

function VisitSummaryView({ rows, loading }) {
  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">Loading visit summary…</Typography>
      </Box>
    );
  }

  const flatData = rows.map((r, index) => {
    const formatTime = (isoStr) => {
      if (!isoStr) return 'N/A';
      return new Date(isoStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const calcDuration = (start, end) => {
      if (!start || !end) return 'N/A';
      const diffMs = new Date(end).getTime() - new Date(start).getTime();
      if (diffMs < 0) return 'N/A';
      const hrs = Math.floor(diffMs / 3600000);
      const mins = Math.floor((diffMs % 3600000) / 60000);
      return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
    };

    const inTime = formatTime(r.checked_in_at);
    const outTime = formatTime(r.checked_out_at);
    const duration = calcDuration(r.checked_in_at, r.checked_out_at);
    const statusText = r.status ? r.status.charAt(0).toUpperCase() + r.status.slice(1) : 'Pending';

    let detailStr = `Check-in: ${inTime} | Check-out: ${outTime} | Time Spent: ${duration}`;
    if (r.school_review) {
      detailStr += `<br/><br/><b>Review:</b><br/>${r.school_review}`;
    }

    return {
      id: r.id || index,
      date: r.visit_date || 'Unknown Date',
      company: r.company_name || 'Unknown Company',
      user: r.assigned_to_name || 'Unassigned',
      status: statusText,
      details: detailStr
    };
  });

  if (flatData.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No visits found in this date range.</Typography>
      </Box>
    );
  }

  const formatDate = (val) => {
    if (!val || val === 'Unknown Date') return val;
    const [y, m, d] = val.split('-');
    return d && m && y ? `${d}-${m}-${y}` : val;
  };

  return (
    <Grid container spacing={3}>
      {flatData.map((row) => (
        <Grid item xs={12} md={6} lg={4} key={row.id}>
          <Card elevation={0} sx={{ borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.2 }}>
                  {row.company}
                </Typography>
                <Chip label={formatDate(row.date)} size="small" color="primary" variant="outlined" sx={{ fontWeight: 600 }} />
              </Box>

              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                    Assigned To
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {row.user}
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                    Status
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: row.status === 'Visited' ? 'success.main' : 'text.primary' }}>
                    {row.status}
                  </Typography>
                </Box>
              </Stack>
              
              <Divider sx={{ my: 1.5 }} />

              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  Visit Details
                </Typography>
                <ExpandableCell value={row.details || ''} isHtml />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}

export default function VisitSummaryTab(props) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const defaultStartDate = new Date();
  defaultStartDate.setDate(defaultStartDate.getDate() - 15);
  const defaultEndDate = new Date();
  defaultEndDate.setDate(defaultEndDate.getDate() + 15);

  const formatDateForInput = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const [startDate, setStartDate] = useState(props.companyId ? '' : formatDateForInput(defaultStartDate));
  const [endDate, setEndDate] = useState(props.companyId ? '' : formatDateForInput(defaultEndDate));
  const [statusFilter, setStatusFilter] = useState('all');

  const loadRows = () => {
    setLoading(true);

    const params = {
      limit: 1000,
      pageno: 1,
    };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
    if (props.companyId) params.company = props.companyId;
    if (props.mine) params.mine = '1';

    getRequest(GET_URL.company_visits.api, params, props)
      .then((response) => {
        if (response && response.status === 200) {
          const data = response.data?.data || [];
          setRows(Array.isArray(data) ? data : []);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadRows();
    // eslint-disable-next-line
  }, [startDate, endDate, statusFilter, props.companyId]);

  return (
    <Box sx={{ mt: 3 }}>
      {!props.companyId && (
        <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: '16px', boxShadow: '0 6px 20px rgba(0,0,0,0.05)' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <DateRange
                label="Visit Date Range"
                startDate={startDate}
                endDate={endDate}
                handleChange={(val) => {
                  if (val && val.start && val.end) {
                    setStartDate(val.start);
                    setEndDate(val.end);
                  } else {
                    setStartDate('');
                    setEndDate('');
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                variant="outlined"
                size="small"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="visited">Visited</MenuItem>
                <MenuItem value="postponed">Postponed</MenuItem>
                <MenuItem value="cancel">Canceled</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </Paper>
      )}

      <VisitSummaryView rows={rows} loading={loading} />
    </Box>
  );
}
